/**
 * Centralized navigation data for the Partner Portal platform.
 *
 * This is the single source of truth for all sidebar navigation items AND
 * route-prefix ownership across every SPA in the platform.
 *
 * To add a new nav item:
 *  1. Add it to sidebar.json.
 *  2. Add its route prefix to the owning SPA's key in APP_ROUTE_PREFIXES below.
 *  3. No changes needed in any other repo — all SPAs import from @lucid/lego.
 */
import type { NavigationItem } from './Sidebar';
import sidebarJson from './sidebar.json';

export const navigationItems: NavigationItem[] = sidebarJson.data as NavigationItem[];

/**
 * Union of every navigation item ID defined in sidebar.json, plus planned
 * workspaces not yet added (vehicle-hub, claims-center).
 *
 * Use this type in any app's ROLE_ENTITLEMENTS map so TypeScript catches
 * typos and missing entries at compile time.
 *
 * Kept here because sidebar.json is the single source of truth for nav IDs —
 * when a new item is added to the sidebar, add its ID to this union too.
 */
export type NavEntitlement =
  // ── Top-level workspaces ───────────────────────────────────────────────
  | 'admin-panel'
  | 'prospect-desk'
  | 'order-hub'
  | 'parts-center'
  | 'service-center'
  | 'customer-care'
  | 'registration-desk'
  | 'accounts-desk'
  // planned — not yet in sidebar.json
  | 'vehicle-hub'
  | 'claims-center'
  // ── Admin Panel children ───────────────────────────────────────────────
  | 'workspace-access'
  | 'portal-setup'
  | 'test-drive-scheduler'
  // ── Prospect Desk children ─────────────────────────────────────────────
  | 'prospects'
  | 'create-new-prospect'
  | 'test-drive-calendar'
  // ── Order Hub children ─────────────────────────────────────────────────
  | 'order-builder'
  | 'manage-orders'
  | 'order-desk'
  // ── Parts Center children ──────────────────────────────────────────────
  | 'parts-ordering'
  | 'track-orders'
  | 'publications'
  // ── Service Center children ────────────────────────────────────────────
  | 'work-order-desk'
  | 'fse-tech-support'
  | 'claims'
  | 'fhm-alerts'
  | 'campaigns'
  | 'locate-asset'
  // ── Customer Care children ─────────────────────────────────────────────
  | 'case-tracker'
  | 'create-case'
  | 'saved-cases'
  | 'inbound-cases'
  // ── Registration Desk children ─────────────────────────────────────────
  | 'registration-overview'
  | 'vehicle-registration'
  // ── Accounts Desk children ─────────────────────────────────────────────
  | 'credit-overview'
  | 'invoices';

/**
 * Centralized route-prefix registry for cross-app navigation.
 *
 * The Sidebar uses the relevant key to decide whether a nav click should be
 * handled by React Router (client-side) or trigger a full page load (cross-app).
 *
 * Rules:
 *  - Root path `/` is always client-side (handled as a special case in Sidebar).
 *  - Any path whose prefix matches an entry in the app's own key → client-side navigate.
 *  - Everything else → `location.href` full page load.
 */
export const APP_ROUTE_PREFIXES = {
  /**
   * Routes owned by lucid-partner-portal.
   * '/' represents the root/dashboard route — it is matched as an exact path in Sidebar,
   * not as a startsWith prefix, so it does not accidentally match all routes.
   */
  partnerPortal: [
    '/',
    '/admin',
    '/prospect-desk',
    '/test-drive-calendar',
    '/order-hub',
    '/login',
    '/accept-invitation',
  ],

  /**
   * Routes owned by lucid-partner-portal-services-frontend.
   */
  services: [
    '/parts-center',
    '/service-center',
    '/claims-center',
    '/vehicle-hub',
    '/customer-care',
    '/inbox',
    '/notifications',
    '/track-orders',
  ],

  /**
   * Routes owned by lucid-partner-portal-importer-frontend.
   */
  importer: ['/registration-desk', '/accounts-desk'],
};

export type AppKey = keyof typeof APP_ROUTE_PREFIXES;

/**
 * Maps Auth0 role names (as they appear in the ID token) to sidebar role IDs.
 *
 * Auth0 role names come from: https://{AUTH0_DOMAIN}/roles claim in the ID token.
 * Sidebar role IDs are defined in roles.json.
 *
 * To add a new mapping:
 *  1. Create the role in Auth0 and assign it to users.
 *  2. Add the entry here: 'Auth0RoleName' → 'sidebar-role-id'.
 *  3. No changes needed in Sidebar.tsx.
 */
export const AUTH0_ROLE_MAP: Record<string, string> = {
  // Lucid
  'Lucid Admin': 'lucid-admin',

  // Importer
  'Importer-Admin-R1': 'importer-l1-admin',
  'Importer-Manager-R2': 'importer-l2-manager',
  'Importer-User-R3': 'importer-l3',

  // Agent / Agency
  'Agency-Admin-R4': 'agent-l1-admin',
  'Agency-Manager-R5': 'agent-l2-manager',
  'Agency-User-R6': 'agent-l3',

  // AR
  AR_L2_Manager: 'ar-l2-manager',
  AR_L3_Technician: 'ar-l3-technician',

  // Not yet in Auth0 — add entries here once created:
  // 'Lucid-HQ':                        'lucid-hq',
  // 'Importer-Service-Manager':        'importer-service-manager',
  // 'Importer-L4-Technician':          'importer-l4-technician',
  // 'Importer-Group-Service-Manager':  'importer-group-service-manager',
};
